<script setup lang="ts">
/**
 * Module: web/src/components/entry/CoordinateInputs.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.1
 * Date: 2026-10-30
 *
 * v1.0.1 (exact-coordinates review fixes): a field's own format or degrees-and-minutes error now shows before "Enter both latitude and longitude." (the commit verdict is readCoordFields in lib/coordinates, where the Node test covers it, and a space-separated pair in one field needs decimals on both numbers); after the one-tap fix applies, focus moves to Latitude instead of falling to the page; an invalid pair also shows on the inputs (error border color).
 *
 * The Latitude / Longitude pair (group label "Coordinates") -- ONE component
 * for every surface that edits pickup coordinates (contracts
 * assisted-entry-api v1.6.0, entry-log-api v1.0.3): MapEntry renders it, so
 * the entry wizard and the draft review page get it in one place, and the
 * Entry Detail modal renders it directly.
 *
 * The PARENT stays the single source of truth for numbers; this component
 * keeps two string buffers and commits a whole, in-range pair:
 *   - synchronously when focus leaves the pair (Safari never focuses a tapped
 *     button, so a null relatedTarget commits at once and the button's click
 *     handler then reads the committed value), on Enter in Longitude, on a
 *     pasted pair or map link, on flush(), and on unmount with a valid pair;
 *     never per keystroke (each commit recenters the map). Enter / "next" in
 *     Latitude only moves to Longitude, so an old longitude is never paired
 *     with a new latitude
 *   - a half or invalid pair never commits: the error shows inline and
 *     flush() returns ok:false, so Next / Submit / Approve / Save stop
 *   - an untouched blur or a retyped identical value never relabels a lookup
 *     or a pin as exact; blanking both fields clears the location only when
 *     the fields still showed the parent's pair (a lookup that landed while
 *     the person typed refills instead)
 *   - a pasted pair or map link fills both fields whatever they held; a
 *     lookup- or pin-filled field selects its value on focus, so typing
 *     replaces it instead of appending
 *   - soft notes for committed exact coordinates (outside the continental US,
 *     with a one-tap fix; fewer than 4 decimals) -- never blocking
 *
 * Keyboard: type="text" + inputmode="text" on both inputs. The iOS decimal
 * pad has no minus key and every Texas longitude is negative; the full
 * keyboard's "123" plane has both "-" and ".". Never type="number" (Vue casts
 * the value, the browser refuses a pasted pair, the mouse wheel changes it).
 * .field-input keeps the 48px height and 16px text (no iOS focus zoom).
 */
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';
import HelpTip from '@/components/HelpTip.vue';
import {
  EXACT_COORDINATES,
  RANGE_MESSAGE,
  exactPointNote,
  findCoordPair,
  fixHint,
  formatCoord,
  inConus,
  inRange,
  normalizeCoordChars,
  normalizeCoordText,
  parseCoordText,
  readCoordFields,
  sameCoord,
  suggestFix,
  type CoordFix,
} from '@/lib/coordinates';

const props = defineProps<{
  lat: number | null;
  lon: number | null;
  /** The stored location precision of lat/lon ('coordinates', a geocoder tier, 'manual', ...). */
  confidence: string | null;
  /** HelpTip registry key: 'dispatch.coordinates' (MapEntry) or 'mo.entry_coordinates' (modal). */
  tipKey: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  /** A whole, in-range pair, already rounded to 7 decimals. */
  (e: 'commit', value: { lat: number; lon: number }): void;
  /** Both fields blanked over the parent's pair: remove the location. */
  (e: 'clear'): void;
  /** True from the first keystroke until a commit, a clear or a refill. */
  (e: 'update:dirty', value: boolean): void;
}>();

const labelId = useId();
const latId = useId();
const lonId = useId();
const msgId = useId();

const groupEl = ref<HTMLElement | null>(null);
const latEl = ref<HTMLInputElement | null>(null);
const lonEl = ref<HTMLInputElement | null>(null);

const latText = ref('');
const lonText = ref('');
const dirty = ref(false);
/** A failed commit left the pair: the next parent change may refill it. */
const stale = ref(false);
const error = ref('');
const errorFix = ref<CoordFix | null>(null);
/** The parent values the buffers last displayed. */
let shownLat: number | null = null;
let shownLon: number | null = null;
/** Per field: typed into since the buffers last showed the parent's pair
 *  (an untouched field still holds a lookup / pin value -> select on focus). */
const touched = { lat: false, lon: false };

function setDirty(v: boolean): void {
  if (dirty.value === v) return;
  dirty.value = v;
  emit('update:dirty', v);
}

function clearError(): void {
  error.value = '';
  errorFix.value = null;
}

/** Show the parent's pair (a lookup, a pin, a draft load, a reset). */
function refill(): void {
  latText.value = formatCoord(props.lat);
  lonText.value = formatCoord(props.lon);
  shownLat = props.lat;
  shownLon = props.lon;
  touched.lat = false;
  touched.lon = false;
  clearError();
  stale.value = false;
  setDirty(false);
}

// Immediate: MapEntry remounts on every return to wizard step 2. Never
// clobber what the person is typing, except a pair a failed commit left.
watch(
  [() => props.lat, () => props.lon],
  () => {
    if (!dirty.value || stale.value) refill();
  },
  { immediate: true },
);

/** Put a person-supplied pair (typed, pasted, split, a fix) into both fields. */
function fillPair(lat: number, lon: number): void {
  latText.value = formatCoord(lat);
  lonText.value = formatCoord(lon);
  touched.lat = true;
  touched.lon = true;
}

function fail(message: string, fix: CoordFix | null = null): boolean {
  error.value = message;
  errorFix.value = fix;
  stale.value = true;
  return false;
}

/**
 * Commit the buffers. Returns true when nothing is pending afterwards
 * (committed, cleared, refilled, or nothing was typed); false when a half or
 * invalid pair remains (its error shows inline).
 */
function commit(): boolean {
  if (!dirty.value) return true;
  // readCoordFields: a pair or link in either field wins (Latitude's when
  // both); a field's own format or degrees-and-minutes error comes before
  // "Enter both latitude and longitude."; both values rounded to 7 decimals.
  const read = readCoordFields(latText.value, lonText.value);
  if (read.kind === 'empty') {
    clearError();
    stale.value = false;
    setDirty(false);
    if (sameCoord(props.lat, shownLat) && sameCoord(props.lon, shownLon)) {
      // Blanking both fields removes the location, the same as "Remove pin".
      if (props.lat !== null || props.lon !== null) emit('clear');
    } else {
      // The parent changed while the person typed (a lookup landed): show it.
      refill();
    }
    return true;
  }
  if (read.kind === 'error') return fail(read.message);
  const { lat, lon } = read;
  fillPair(lat, lon);
  if (!inRange(lat, lon)) {
    const fix = suggestFix(lat, lon);
    return fail(fix ? `${RANGE_MESSAGE} ${fixHint(fix)}` : RANGE_MESSAGE, fix);
  }
  clearError();
  stale.value = false;
  touched.lat = false;
  touched.lon = false;
  setDirty(false);
  if (sameCoord(lat, props.lat) && sameCoord(lon, props.lon)) {
    // Unchanged: an untouched blur never relabels a lookup or a pin as exact.
    shownLat = props.lat;
    shownLon = props.lon;
    return true;
  }
  shownLat = lat;
  shownLon = lon;
  emit('commit', { lat, lon });
  return true;
}

function markTyping(): void {
  setDirty(true);
  stale.value = false;
  clearError();
}

function onInput(which: 'lat' | 'lon', event: Event): void {
  const el = event.target as HTMLInputElement;
  markTyping();
  touched[which] = true;
  const inputType = (event as InputEvent).inputType ?? '';
  if (inputType === 'insertFromDrop' || inputType === 'insertReplacementText') {
    // Drag-and-drop or a keyboard suggestion inserting a whole pair: split and commit.
    const pair = findCoordPair(el.value);
    if (pair) {
      fillPair(pair.lat, pair.lon);
      commit();
      return;
    }
  }
  const raw = el.value;
  const norm = normalizeCoordChars(raw);
  if (norm !== raw) {
    // Keep the caret where it was (minus look-alikes, the Android "30. 5" space).
    const caret = Math.max(0, (el.selectionStart ?? raw.length) + norm.length - raw.length);
    el.value = norm;
    el.setSelectionRange(caret, caret);
  }
  if (which === 'lat') latText.value = norm;
  else lonText.value = norm;
}

function onPaste(which: 'lat' | 'lon', event: ClipboardEvent): void {
  const text = event.clipboardData?.getData('text/plain') ?? '';
  if (!text) return;
  const pair = findCoordPair(text);
  if (pair) {
    // A pair or map link fills BOTH fields, whatever they held (the iOS
    // callout pastes at the caret, which would glue it onto a lookup value).
    event.preventDefault();
    markTyping();
    fillPair(pair.lat, pair.lon);
    commit();
    return;
  }
  if (!touched[which] && parseCoordText(text, which).kind === 'one') {
    // One number into an untouched field replaces its value.
    event.preventDefault();
    markTyping();
    touched[which] = true;
    if (which === 'lat') latText.value = normalizeCoordText(text);
    else lonText.value = normalizeCoordText(text);
  }
}

function onFocus(which: 'lat' | 'lon', event: FocusEvent): void {
  // A lookup or pin value is selected, so a typed or pasted value replaces it.
  if (touched[which] || props.confidence === EXACT_COORDINATES) return;
  const el = event.target as HTMLInputElement;
  if (!el.value) return;
  el.select();
  // iOS ignores select() inside focus; re-apply after the tap settles.
  window.setTimeout(() => {
    if (document.activeElement === el && !touched[which]) el.setSelectionRange(0, el.value.length);
  }, 0);
}

function onFocusOut(event: FocusEvent): void {
  const next = event.relatedTarget as Node | null;
  // Latitude -> Longitude (or the help tip) stays inside the pair: no commit.
  if (next && groupEl.value?.contains(next)) return;
  commit();
}

function onLatEnter(): void {
  // Split a pair or link typed into Latitude; never commit from here.
  const p = parseCoordText(latText.value, 'lat');
  if (p.kind === 'pair') fillPair(p.lat, p.lon);
  lonEl.value?.focus();
}

function onLonEnter(event: KeyboardEvent): void {
  if (commit()) (event.target as HTMLInputElement).blur(); // closes the phone keyboard
}

/** Soft note for committed exact coordinates (never while typing). */
const note = computed(() => (dirty.value ? null : exactPointNote(props.lat, props.lon, props.confidence)));

/** The one-tap fix: the error's, else the outside-the-US note's. */
const fix = computed<CoordFix | null>(() => {
  if (error.value) return errorFix.value;
  if (!note.value || props.lat === null || props.lon === null || inConus(props.lat, props.lon)) return null;
  return suggestFix(props.lat, props.lon);
});

function applyFix(): void {
  const f = fix.value;
  if (!f || props.disabled) return;
  markTyping();
  fillPair(f.lat, f.lon);
  commit();
  // The fix button leaves with the problem it fixed; keep focus in the pair
  // instead of letting it fall to the page. After the re-render, so the
  // parent's new precision is in place and the value is not selected.
  void nextTick(() => latEl.value?.focus());
}

/** Parents call this before Next / Submit / Approve / Save. */
function flush(): { ok: boolean } {
  return { ok: commit() };
}

defineExpose({ flush });

onBeforeUnmount(() => {
  if (!dirty.value) return;
  commit(); // a valid pair lands (wizard Back, a step jump, leaving the page)
  setDirty(false); // a half or invalid pair is dropped
});
</script>

<template>
  <div ref="groupEl" role="group" :aria-labelledby="labelId" @focusout="onFocusOut">
    <span class="field-label">
      <span class="whitespace-nowrap"><span :id="labelId">Coordinates</span> <HelpTip :k="tipKey" /></span>
    </span>
    <div class="grid grid-cols-2 gap-3 items-end sm:max-w-md">
      <div class="min-w-0">
        <label :for="latId" class="block text-xs text-fg-muted mb-1">Latitude</label>
        <input
          :id="latId"
          ref="latEl"
          :value="latText"
          class="field-input"
          type="text"
          inputmode="text"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          enterkeyhint="next"
          placeholder="e.g. 30.030731"
          :disabled="disabled"
          :aria-invalid="error ? 'true' : undefined"
          :aria-describedby="msgId"
          data-testid="coord-lat"
          @input="onInput('lat', $event)"
          @paste="onPaste('lat', $event)"
          @focus="onFocus('lat', $event)"
          @keydown.enter.prevent="onLatEnter"
        />
      </div>
      <div class="min-w-0">
        <label :for="lonId" class="block text-xs text-fg-muted mb-1">Longitude</label>
        <input
          :id="lonId"
          ref="lonEl"
          :value="lonText"
          class="field-input"
          type="text"
          inputmode="text"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          enterkeyhint="done"
          placeholder="e.g. -97.771006"
          :disabled="disabled"
          :aria-invalid="error ? 'true' : undefined"
          :aria-describedby="msgId"
          data-testid="coord-lon"
          @input="onInput('lon', $event)"
          @paste="onPaste('lon', $event)"
          @focus="onFocus('lon', $event)"
          @keydown.enter.prevent="onLonEnter"
        />
      </div>
    </div>
    <div :id="msgId" aria-live="polite">
      <p v-if="error" class="mt-1 text-xs text-accent-bad" data-testid="coord-error">{{ error }}</p>
      <p v-else-if="note" class="mt-1 text-xs text-accent-warm" data-testid="coord-note">{{ note }}</p>
      <button
        v-if="fix"
        type="button"
        class="btn-secondary mt-1.5 !h-11 !px-3 !text-sm"
        :disabled="disabled"
        data-testid="coord-fix"
        @click="applyFix"
      >
        Use {{ formatCoord(fix.lat) }}, {{ formatCoord(fix.lon) }}
      </button>
    </div>
  </div>
</template>

<style scoped>
/* An invalid pair shows on the inputs too, not only in the message: the
   error color as the border, in both themes (focused or not). */
.field-input[aria-invalid='true'] {
  border-color: rgb(var(--accent-bad-rgb));
}
</style>
